import { Injectable } from "@angular/core";
import { Observable, Subject } from "rxjs";

/**
 * In-memory sibling list for Pharmacy Review detail Previous/Next only.
 * Also remembers the last Pharmacy Review billable month (not the invoice rows).
 */
@Injectable({ providedIn: 'root' })
export class InvoiceCacheService {
  private static readonly BILLING_MONTH_KEY = 'solvo.invoiceReview.billingMonth';

  private invoiceList: any[] = [];
  private selectedBillingMonth: string = null;
  private billingMonthResetFromMenu$ = new Subject<void>();

  setInvoices(invoices: any[]) {
    this.invoiceList = invoices ? invoices : [];
  }

  getInvoices(): any[] {
    return this.invoiceList;
  }

  /** Persist last selected Billable Month (YYYY-MM-DD first of month). */
  setSelectedBillingMonth(billingDate: string): void {
    if (!billingDate) {
      return;
    }
    this.selectedBillingMonth = billingDate;
    try {
      if (typeof sessionStorage !== 'undefined' && sessionStorage != null) {
        sessionStorage.setItem(InvoiceCacheService.BILLING_MONTH_KEY, billingDate);
      }
    } catch (e) {
    }
  }

  getSelectedBillingMonth(): string {
    if (this.selectedBillingMonth) {
      return this.selectedBillingMonth;
    }
    try {
      if (typeof sessionStorage !== 'undefined' && sessionStorage != null) {
        const saved = sessionStorage.getItem(InvoiceCacheService.BILLING_MONTH_KEY);
        if (saved) {
          this.selectedBillingMonth = saved;
          return saved;
        }
      }
    } catch (e) {
    }
    return null;
  }

  /** Clear saved month (used when opening Pharmacy Review from the menu). */
  clearSelectedBillingMonth(): void {
    this.selectedBillingMonth = null;
    try {
      if (typeof sessionStorage !== 'undefined' && sessionStorage != null) {
        sessionStorage.removeItem(InvoiceCacheService.BILLING_MONTH_KEY);
      }
    } catch (e) {
    }
  }

  /**
   * Menu entry: clear saved month and notify an already-open Pharmacy Review page
   * to reload the default previous month.
   */
  resetBillingMonthFromMenu(): void {
    this.clearSelectedBillingMonth();
    this.billingMonthResetFromMenu$.next();
  }

  onBillingMonthResetFromMenu(): Observable<void> {
    return this.billingMonthResetFromMenu$.asObservable();
  }

  getNextInvoice(currentId: number, pharmacyId: number, facilityId: number, patientId: number): any | null {
    const index = this.findCurrentInvoiceIndex(currentId, pharmacyId, facilityId, patientId);
    return index >= 0 && index < this.invoiceList.length - 1
      ? this.invoiceList[index + 1]
      : null;
  }

  getPreviousInvoice(currentId: number, pharmacyId: number, facilityId: number, patientId: number): any | null {
    const index = this.findCurrentInvoiceIndex(currentId, pharmacyId, facilityId, patientId);
    return index > 0
      ? this.invoiceList[index - 1]
      : null;
  }

  findCurrentInvoiceIndex(currentId: number, pharmacyId: number, facilityId: number, patientId: number): number {
    const id = Number(currentId);
    const pharmacy = Number(pharmacyId);
    const facility = Number(facilityId);
    const patient = Number(patientId);
    return this.invoiceList.findIndex(i =>
      Number(i.id) === id
      && Number(i.pharmacyId) === pharmacy
      && Number(i.facilityId) === facility
      && Number(i.patientId) === patient
    );
  }
}
